import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { usePayments } from '../../Payments/hooks/usePayments';
import { useProjects } from '../../Projects/hooks/useProjects';
import { useStudents } from '../../Students/hooks/useStudents';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { MONTHS } from '../../../shared/constant/months';
import { compareByGradeOrder } from '../../../shared/utils/sortByGrade';
import { buildFinancialReport } from '../utils/buildFinancialReport';
import { downloadReportPdf } from '../utils/downloadReportPdf';
import FinancialReport from './FinancialReport';

const REPORT_LABELS = {
  collections: { title: 'Collections', filenamePart: 'Collections' },
  disbursement: { title: 'Disbursement', filenamePart: 'Disbursements' },
  summary: { title: 'Summary (Collections and Disbursement)', filenamePart: 'Summary' },
};

function PrintRecordPage({ view = 'summary' }) {
  const { payments, loading: paymentsLoading } = usePayments();
  const { projects, loading: projectsLoading } = useProjects();
  const { students, loading: studentsLoading } = useStudents();

  const [schoolYear, setSchoolYear] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [section, setSection] = useState('');
  const [generatedAt] = useState(() => new Date());
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  const loading = paymentsLoading || projectsLoading || studentsLoading;
  const showStudentFilters = view === 'collections';

  const schoolYearOptions = useMemo(
    () => [...new Set(students.map((s) => s.school_year).filter(Boolean))].sort().reverse(),
    [students]
  );

  const sectionOptions = useMemo(() => {
    const source = gradeLevel ? students.filter((s) => s.grade_level === gradeLevel) : students;
    return [...new Set(source.map((s) => s.section).filter(Boolean))].sort(compareByGradeOrder);
  }, [students, gradeLevel]);

  useEffect(() => {
    if (section && !sectionOptions.includes(section)) {
      setSection('');
    }
  }, [sectionOptions, section]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    for (const p of payments) if (p.payment_date) years.add(p.payment_date.slice(0, 4));
    for (const p of projects) if (p.expense_date) years.add(p.expense_date.slice(0, 4));
    return [...years].sort().reverse();
  }, [payments, projects]);

  const filters = {
    schoolYear,
    year,
    month,
    gradeLevel: showStudentFilters ? gradeLevel : '',
    section: showStudentFilters ? section : '',
  };

  const report = useMemo(
    () => buildFinancialReport({ payments, projects, students, filters }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, projects, students, schoolYear, year, month, gradeLevel, section, showStudentFilters]
  );

  const hasActiveFilters = Boolean(
    schoolYear || year || month || (showStudentFilters && (gradeLevel || section))
  );
  const clearFilters = () => {
    setSchoolYear('');
    setYear('');
    setMonth('');
    setGradeLevel('');
    setSection('');
  };

  const reportLabel = REPORT_LABELS[view] ?? REPORT_LABELS.summary;

  const handleDownload = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const safePeriod = report.periodLabel.replace(/[^\w-]+/g, '_');
      await downloadReportPdf(reportRef.current, `SPTA-${reportLabel.filenamePart}-${safePeriod}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-1">
          <h2 className="text-lg font-semibold text-white">{reportLabel.title}</h2>
          <p className="text-sm text-gray-200">Generate a printable SPTA financial statement</p>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 w-full">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Months</option>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Years</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <select
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All School Years</option>
              {schoolYearOptions.map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>

            {showStudentFilters && (
              <>
                <select
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  <option value="">All Grades</option>
                  {GRADE_LEVELS.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>

                <select
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  <option value="">All Sections</option>
                  {sectionOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </>
            )}

            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="text-lg text-gray-500 border border-gray-300 px-3 py-2 rounded-md hover:text-gray-900 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear filters
            </button>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading || downloading}
              className="flex items-center justify-center gap-1.5 rounded-md bg-gold text-navy text-lg font-medium px-4 py-2 hover:bg-gold-dark disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloading ? 'Generating PDF…' : 'Download PDF'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading data…</p>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8 overflow-x-auto">
          <div ref={reportRef}>
            <FinancialReport report={report} generatedAt={generatedAt} view={view} />
          </div>
        </div>
      )}
    </section>
  );
}

export default PrintRecordPage;
